import React, { useState, useEffect } from 'react';
import { Table, Button, Icon, Modal, Header, Message, Toast, Pagination, Label } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import { Expense, SetLoading, Asset } from '../SiteClasses';
import { addCommas, bindClose, bindClick, getDepartmentName } from '../SiteUtil';
import { useUser } from '../hooks';
import { firstBy } from 'thenby';
import { ExpenseModal, InfoLog, AddAssetModal } from './index';
import { Link } from 'react-router-dom';
import UserAccess from '../UserAccess';
import classNames from 'classnames';
import API from '../API';

interface ExpensesTableProps {
	dateId: number;
	eventName: string;
}

enum Modals{
	INFO = 1,
	APPROVE = 2,
	INFO_LOG = 3,
	EDIT = 4,
	ASSET = 5,
}

const PAGE_SIZE = 5;

var ExpensesTable = (props: ExpensesTableProps)=>{
	var { hasAccess } = useUser();
	var [expenses, setExpenses] = useState<Expense[]>([]);
	var [loading, setLoading] = useState<boolean>(true);
	var [page, setPage] = useState<number>(0);
	var [showExpenseModal, setShowExpenseModal] = useState<boolean>(false);
	var [selectedExpense, setSelectedExpense] = useState<Expense>(null);
	var [shownModal, setShownModal] = useState<number>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);

	useEffect(()=>{
		getExpenses();
	}, [props.dateId]);

	var getExpenses = (get_page?: number)=>{
		var offset = (get_page > 0) ? Math.max(0, (get_page || page) * PAGE_SIZE) : 0;
		
		setLoading(true);
		API.getEventExpenses(props.dateId, PAGE_SIZE, offset).then(res=>{
			if(res.error) return Toast.error(res.message);
			setExpenses(res.data);
		}).catch(err=>{
			Toast.error('Hubo un error cargando los gastos (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onPageChange = (page: number)=>{
		setLoading(true);
		setPage(page);
		getExpenses(page);
	};

	var onAddExpense = ()=>{
		setShowExpenseModal(true);
	};

	var onExpenseCreated = (expense: Expense)=>{
		setExpenses(prev => [...prev, expense]);
		setShowExpenseModal(false);
	};

	var onExpenseUpdated = (expense: Expense)=>{
		setExpenses(prev => prev.map(e => 
			e.expense_id === expense.expense_id ? expense : e
		));
		setShownModal(null);
	};

	var onAssetAdded = (asset: Asset)=>{
		
		Toast.success('Evidencia agregada exitosamente');
		setShownModal(null);
	};

	var onShowInfo = (expense: Expense)=>{
		setSelectedExpense(expense);
		setShownModal(Modals.INFO);
	};

	var onShowApprove = (expense: Expense)=>{
		setSelectedExpense(expense);
		setShownModal(Modals.APPROVE);
	};

	var approveExpense = (setLoading: SetLoading)=>{
		setLoading(true);
		API.approveExpense([selectedExpense.expense_id]).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha aprobado el gasto');
			
			setExpenses(prev => prev.map(e => 
				e.expense_id === selectedExpense.expense_id ? { ...e, approved: true } : e
			));
			setShownModal(null);
			setSelectedExpense(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado actualizando el gasto (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	return <>
		<Table
			fitted
			striped
			title='Gastos'
			headers={['ID', 'Titulo', 'Tipo', 'Departamento', 'Estatus', 'Monto', <Icon name='wrench' />]}
			style={{ maxWidth: 800, margin: 'auto', marginTop: 20 }}
			button={hasAccess(UserAccess.EVENTS.CREATE_EXPENSE) && (
			<Button 
				text='Agregar' 
				color='black' 
				iconName='plus-circle' 
				size='tiny' 
				onClick={onAddExpense}
			/>
		)}
			footer={
				(!!expenses && expenses.length >= PAGE_SIZE || page !== 0) && (
					<Table.Cell colSpan={10} row>
						<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
							<Pagination page={page} onPageChange={onPageChange} hasNext={expenses.length>=PAGE_SIZE} />
						</div>
					</Table.Cell>
				)
			}
		>
		{loading ? (
			<Table.Row>
				<Table.Cell colSpan={7}>
					<Header loading text='Cargando datos...' />
				</Table.Cell>
			</Table.Row>
		) : expenses.length !== 0 ? (
			expenses.sort(firstBy('paid', 'asc').thenBy('approved')).map(e => (
				<Table.Row
					key={e.expense_id}
					collapsingIndexes={[0, 2, 3, 4, 5, 6]}
				centeredIndexes={[3, 5]}
					data={[
						e.expense_id,
						e.expense_title,
						e.type_name,
						e.department_id ? <Label style={{ width: 110}} color='orange'>{getDepartmentName(e.department_id)}</Label> : <Icon name='minus'/>,
						<div className={classNames("fr label", {
							green: e.paid,
							blue: !e.paid && e.approved,
							red: e.rejected && !e.paid,
							yellow: !e.paid && !e.approved && !e.rejected,
						})}>
							{e.paid ? 'Pagado' : e.approved ? 'Aprobado' : e.rejected ? 'Rechazado' : 'Pendiente'}
						</div>,
						'$' + addCommas(e.amount),
						<Dropdown direction='left' icon={(
							<div className="fr label" style={{ padding: '3px 10px' }}>
								<Icon name='ellipsis' style={{ margin: 0 }} />
							</div>
						)} className='icon'>
							<Dropdown.Menu direction='left'>
								<Dropdown.Item onClick={() => onShowInfo(e)}>
									<Icon name="eye" style={{ marginRight: 3 }} /> Info
								</Dropdown.Item>
								{!e.approved && hasAccess(UserAccess.EVENTS.APPROVE_EXPENSE) ? (
								<Dropdown.Item onClick={() => onShowApprove(e)}>
									<Icon name="check" style={{ marginRight: 3 }} /> Aprobar
								</Dropdown.Item>
							) : null}
							{hasAccess(UserAccess.EVENTS.EDIT_EVENTS) && !e.paid && (
								<Dropdown.Item onClick={() => { setSelectedExpense(e); setShownModal(Modals.EDIT); }}>
									<Icon name="pen" style={{ marginRight: 3 }} /> Editar
								</Dropdown.Item>
							)}
							{hasAccess(UserAccess.EVENTS.EDIT_EVENTS) && !e.paid && !e.deleted && (
							<Dropdown.Item onClick={() => { setSelectedExpense(e); setShownModal(Modals.ASSET); }}>
								<Icon name="file" style={{ marginRight: 3 }} /> Agregar Evidencia
							</Dropdown.Item>)}
							<Dropdown.Item onClick={() => { setSelectedExpense(e); setShownModal(Modals.INFO_LOG); }}>
								<Icon name="info" style={{ marginRight: 3 }} /> Ver Logs
							</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					]}
				/>
			))
		) : (
			<Table.Cell row className='empty' colSpan={7}>
				No se encontraron registros
			</Table.Cell>
		)}
		{expenses.length !== 0 && (
			<Table.Row>
				<Table.Cell colSpan={5} style={{ textAlign: 'right', fontSize: 14, fontWeight: 'bold' }}>
					Total:
				</Table.Cell>
				<Table.Cell colSpan={3} style={{ textAlign: 'start', fontSize: 14, fontWeight: 'bold' }}>
					${addCommas(expenses.reduce((sum, e) => sum + e.amount, 0))}
				</Table.Cell>
			</Table.Row>
		)}
		</Table>
		<ExpenseModal
			open={showExpenseModal}
			onClose={bindClose(setShowExpenseModal)}
			onCreate={onExpenseCreated}
			date_id={props.dateId}
			event_name={props.eventName}
		/>
		<Modal open={shownModal === Modals.INFO} size='tiny' onClose={bindClick(setShownModal, null)}>
			<Modal.Header>Información de gasto</Modal.Header>
			{!!selectedExpense && (
				<Modal.Content style={{ paddingBottom: 0 }}>
					<Table
						striped
						details
						fitted
						data={[
							['ID', <>
								{selectedExpense.expense_id}
								<div className='meta'>
									<Button iconName='arrow-up-right-from-square' text='Más información' color='black' size='tiny' as={Link} target='_blank' to={`/expenses/${selectedExpense.expense_id}`}/>
								</div>
							</>],
							['Título', selectedExpense.expense_title],
							['Tipo', selectedExpense.type_name],
							['Departamento', selectedExpense.department_id ? <Label style={{ width: 110 }} color='orange'>{getDepartmentName(selectedExpense.department_id)}</Label> : <Icon name='minus'/>],
							['Monto', selectedExpense.amount],
							['Estatus', <div className={classNames("fr label", {
								green: selectedExpense.paid && !selectedExpense.deleted,
								blue: selectedExpense.approved && !selectedExpense.paid && !selectedExpense.deleted,
								red: selectedExpense.deleted || (selectedExpense.rejected && !selectedExpense.paid),
								yellow: !selectedExpense.paid && !selectedExpense.deleted && !selectedExpense.approved && !selectedExpense.rejected
							})}>
								{selectedExpense.deleted ? 'Eliminado' : selectedExpense.paid ? 'Pagado' : selectedExpense.approved ? 'Aprobado' : selectedExpense.rejected ? 'Rechazado' : 'Pendiente'}
							</div>],
							...(selectedExpense.rejected && !selectedExpense.paid ? [['Razón de rechazo', selectedExpense.reject_reason_name || <Icon name='minus'/>]] : []),
							['Descripción', selectedExpense.description || <Icon name='minus'/>],
						]}
					/>
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={bindClick(setShownModal, null)} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.APPROVE} onClose={bindClick(setShownModal, null)} size='tiny'>
			<Modal.Header>¿Aprobar gasto?</Modal.Header>
			{selectedExpense && <Modal.Content>
				<Header text='¿Marcar presupuesto como aprobado?'/>
				<Table 
					details
					striped
					data={[
						['ID', selectedExpense.expense_id],
						['Titulo', selectedExpense.expense_title],
						['Tipo', selectedExpense.type_name],
						['Departamento', selectedExpense.department_id ? <Label style={{ width: 110 }} color='orange'>{getDepartmentName(selectedExpense.department_id)}</Label> : <Icon name='minus'/>],
						['Monto', selectedExpense.amount],
						['Descripción', selectedExpense.description]
					]}
				/>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShownModal, null)} />
				<Button text='Aprobar' color='black' onClick={approveExpense}/>
			</Modal.Actions>
		</Modal>
		<ExpenseModal
			open={shownModal === Modals.EDIT}
			onClose={bindClick(setShownModal)}
			onUpdate={onExpenseUpdated}
			expenseToEdit={selectedExpense}
			event_name={props.eventName}
			date_id={props.dateId}
		/>
		<AddAssetModal
			expenseId={selectedExpense?.expense_id}
			open={shownModal === Modals.ASSET}
			onClose={bindClick(setShownModal, null)}
			onAdded={onAssetAdded}
		/>
		<InfoLog
			open={shownModal === Modals.INFO_LOG}
			onClose={() => setShownModal(null)}
			data={selectedExpense}
			title="Historial del Gasto"
		/>
	</>
};

export default ExpensesTable;
